import React from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';

import { splitCampusPayment, campusCreditAvailable } from '../../utils/campusPaymentOptions';
import { currentYear } from '../../utils/years';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

/**
 * How much to pay at one campus.
 *
 * Credit belongs to a campus and can only be spent there, so the amount is decided per campus
 * rather than once for the family. Whatever the parent types, that campus's credit is spent
 * first — they cannot hold credit back and put the amount on a card instead — so the split below
 * the box is a statement of what will happen, not another choice.
 *
 * A campus still owed for the current year has that inside its minimum, so the lowest amount the
 * box will take settles it. It is named on its own rather than left to be inferred from a larger
 * minimum, and it is this campus's alone: a debt at one campus is not payable at another, exactly
 * as credit is not.
 */
const CampusPaymentAmount = ({ campus = {}, value, onChange, disabled = false }) => {
    const creditAvailable = campusCreditAvailable(campus);
    const outstandingAmount = Number(campus.outstandingAmount) || 0;
    const minimumAmount = Number(campus.minimumAmount) || 0;
    const fullAmount = Number(campus.fullAmount ?? campus.payableAmount) || 0;

    const typed = value === '' || value === undefined || value === null ? '' : String(value);
    const amount = typed === '' ? minimumAmount : Number(typed);
    const valid = Number.isFinite(amount) && amount >= minimumAmount && amount <= fullAmount;

    const split = splitCampusPayment({ amount: valid ? amount : minimumAmount, creditAvailable });

    return (
        <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ color: '#0f172a', fontWeight: 600, fontSize: '15px' }}>
                    {campus.college}
                </Typography>
                {outstandingAmount > 0 && (
                    <Chip
                        label={`${money(outstandingAmount)} owing for ${currentYear()}`}
                        size="small"
                        sx={{
                            height: '22px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#92400e',
                            bgcolor: '#fffbeb',
                            border: '1px solid #fcd34d',
                        }}
                    />
                )}
                {creditAvailable > 0 && (
                    <Chip
                        label={`Credit available ${money(creditAvailable)}`}
                        size="small"
                        sx={{
                            height: '22px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#065f46',
                            bgcolor: '#ecfdf5',
                            border: '1px solid #6ee7b7',
                        }}
                    />
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    label="Amount to pay"
                    size="small"
                    disabled={disabled}
                    value={typed}
                    onChange={(event) => onChange?.(event.target.value)}
                    error={typed !== '' && !valid}
                    helperText={outstandingAmount > 0
                        ? `Between ${money(minimumAmount)} and ${money(fullAmount)} — includes ${money(outstandingAmount)} owing for ${currentYear()}`
                        : `Between ${money(minimumAmount)} and ${money(fullAmount)}`}
                    inputProps={{
                        inputMode: 'decimal',
                        'aria-label': `Amount to pay at ${campus.college}`,
                    }}
                    sx={{ width: { xs: '100%', sm: '200px' } }}
                />

                <Box sx={{ pt: 0.5 }}>
                    {split.coveredByCredit ? (
                        <>
                            <Typography sx={{ color: '#047857', fontSize: '13px', fontWeight: 600 }}>
                                {`Covered by your ${campus.college} credit — no payment needed`}
                            </Typography>
                            {split.creditRemaining > 0 && (
                                <Typography sx={{ color: '#64748b', fontSize: '12px' }}>
                                    {`${money(split.creditRemaining)} credit will remain at ${campus.college}`}
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography sx={{ color: '#0f172a', fontSize: '13px', fontWeight: 600 }}>
                                {creditAvailable > 0
                                    ? `Paid from credit ${money(split.creditUsed)} · card ${money(split.cardAmount)}`
                                    : `Card payment ${money(split.cardAmount)}`}
                            </Typography>
                            {creditAvailable > 0 && (
                                <Typography sx={{ color: '#64748b', fontSize: '12px' }}>
                                    Your credit is always used first.
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {typed !== '' && !valid && (
                <Typography sx={{ color: '#b91c1c', fontSize: '12px' }}>
                    {`Enter an amount between ${money(minimumAmount)} and ${money(fullAmount)} for ${campus.college}.`}
                </Typography>
            )}
        </Box>
    );
};

export default CampusPaymentAmount;
